import React, {FC} from 'react';
import styled from 'styled-components';

import noResultsImageSrc from '../../../../../../../src/assets/images/noResults.svg';
import {CreateNewGroupPopoverContainer} from '../../../../../../../src/components/contacts/common/createNewGroupPopoverContainer';
import {contactsManagerCardGroupsLearnMoreUrl} from '../../../../../../../src/helpers/contactsManagerHelpers';
import {CardGroupModel} from '../../../../../../../src/models/api/cardGroupModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../../../../src/services/tracking';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {Trans} from '../../../../../../shared/helpers/src/internationalization/react';
import {useInitialEffect} from '../../../../../../shared/helpers/src/react/hooks/useEffectHelpers';
import {Handler, HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../../../shared/ui/src/components/popovers/popoverCoordinator';
import {InfoButton} from '../common/buttons/infoButton';
import {ListsInfoBoxes} from '../common/infobox/listsInfoBoxes';
import {VerticalRule} from '../common/verticalRule';
import {useContactsManagerContext} from '../contactsManagerContext';
import {ContactsManagerHeader} from '../contactsManagerHeader';
import {buildInfoBoxToggleLabel} from '../helpers/infoBoxHelpers';
import {
  buildContactsManagerCreateCardGroupEvent,
  ContactsManagerTrackEventHandler,
} from '../helpers/trackingHelpers';
import {ContactsManagerTableEmptyState} from '../tables/common/contactsManagerEmptyState';
import {ListsTableContainer} from '../tables/lists/listsTableContainer';

/*
 * Props.
 */

export interface CardGroupsPageProps {
  isInfoBoxExpanded: boolean;
  onCreateSuccess: HandlerOf<CardGroupModel | undefined>;
  onToggleInfoBox: Handler;
}

/*
 * Styles.
 */

const StyledImg = styled.img`
  width: 92px;
`;

const StyledDescription = styled.div`
  width: 247px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  padding: 0 3px 0 3px;
`;

const StyledCardGroupIcon = styled(Icon)`
  margin-right: 8px;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  height: inherit;
`;

/*
 * Component.
 */

export const CardGroupsPage: FC<CardGroupsPageProps> = ({
  onCreateSuccess,
  isInfoBoxExpanded,
  onToggleInfoBox,
}) => {
  const {panelId, onTrackHandler} = useContactsManagerContext();

  useInitialEffect(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONTACTS_MANAGER_CARD_GROUPS_SECTION_VIEW, {
      list_type: ContactsManagerParametersTypesEnum.LISTS.toLowerCase(),
      is_pinned: false,
    });
  });

  return (
    <>
      <ContactsManagerHeader
        title={<Trans id="card-groups-table-title">Lists</Trans>}
        actions={
          <>
            {renderCreateNewCardGroupPopover(onCreateSuccess, onTrackHandler, true)}
            <VerticalRule />
            <InfoButton
              label={buildInfoBoxToggleLabel()}
              onToggle={onToggleInfoBox}
              isActive={isInfoBoxExpanded}
            />
          </>
        }
      />
      <StyledWrapperDiv>
        <ListsTableContainer
          panelId={panelId}
          renderEmpty={() => renderEmptyState(onCreateSuccess, onTrackHandler)}
        />
        {isInfoBoxExpanded && <ListsInfoBoxes />}
      </StyledWrapperDiv>
    </>
  );
};

/*
 * Helpers.
 */

function renderCreateCardGroupButton(isPrimary: boolean) {
  return (
    <Button variant={isPrimary ? ButtonVariantsEnum.PRIMARY : ButtonVariantsEnum.SECONDARY}>
      <StyledButtonContentDiv>
        <StyledCardGroupIcon name="newGroupSmall" size={16} />
        {isPrimary && <Trans id="contacts-manager-card-group-create">Create list</Trans>}
        {!isPrimary && <Trans id="contacts-manager-card-group-new">New list</Trans>}
      </StyledButtonContentDiv>
    </Button>
  );
}

function renderCreateNewCardGroupPopover(
  onCreateSuccess: HandlerOf<CardGroupModel | undefined>,
  onTrackHandler: ContactsManagerTrackEventHandler,
  isPrimary: boolean,
) {
  return (
    <PopoverCoordinator
      renderButton={() => renderCreateCardGroupButton(isPrimary)}
      renderModal={(popoverProps) => (
        <CreateNewGroupPopoverContainer
          popoverProps={popoverProps}
          onCreateClose={(wasCreated, cardGroup) => {
            popoverProps.onRequestClose();
            // If the create was successful, close the entire popover
            if (wasCreated && cardGroup) {
              onCreateSuccess(cardGroup);
              onTrackHandler(buildContactsManagerCreateCardGroupEvent(cardGroup));
            }
          }}
        />
      )}
    />
  );
}

function renderEmptyState(
  onCreateSuccess: HandlerOf<CardGroupModel | undefined>,
  onTrackHandler: ContactsManagerTrackEventHandler,
) {
  return (
    <ContactsManagerTableEmptyState
      title={<Trans id="card-groups-table-empty-state-title">No lists</Trans>}
      image={<StyledImg alt="card-groups-table-empty-state-image" src={noResultsImageSrc} />}
      description={
        <StyledDescription>
          <Trans id="card-groups-table-empty-state-description">
            Lists make it easy to message multiple contacts at once.
          </Trans>
        </StyledDescription>
      }
      learnMoreUrl={contactsManagerCardGroupsLearnMoreUrl}
    >
      {renderCreateNewCardGroupPopover(onCreateSuccess, onTrackHandler, false)}
    </ContactsManagerTableEmptyState>
  );
}
